import type { CSSProperties } from "react";

/* Hand-drawn heritage ornaments shared across the homepage. All are decorative (aria-hidden). */

// Deterministic pseudo-random so torn edges render identically on every load.
function rng(seed: number) {
  let s = seed % 2147483647;
  if (s <= 0) {
    s += 2147483646;
  }
  return () => (s = (s * 16807) % 2147483647) / 2147483647;
}

/** A jagged torn-paper strip; `edge` is the side that looks torn. */
export function TornEdge({
  edge,
  color,
  rim = "#f6e7c9",
  seed = 7,
  height = 26,
  className,
}: {
  edge: "top" | "bottom";
  color: string;
  rim?: string;
  seed?: number;
  height?: number;
  className?: string;
}) {
  const r = rng(seed);
  const pts: string[] = [];
  const rimPts: string[] = [];
  for (let x = 0; x <= 1000; x += 4 + r() * 9) {
    const base = 4 + r() * (height - 10) + Math.sin(x / 37) * 3;
    pts.push(`${x.toFixed(1)},${base.toFixed(1)}`);
    rimPts.push(
      `${x.toFixed(1)},${Math.max(0, base - 2 - r() * 3).toFixed(1)}`
    );
  }
  pts.push(`1000,${(height / 2).toFixed(1)}`);
  rimPts.push(`1000,${(height / 2 - 2).toFixed(1)}`);
  const body = `M0,${height} L${pts.join(" L")} L1000,${height} Z`;
  const rimPath = `M0,${height} L${rimPts.join(" L")} L1000,${height} Z`;
  return (
    <svg
      className={className}
      aria-hidden="true"
      viewBox={`0 0 1000 ${height}`}
      preserveAspectRatio="none"
      style={{ transform: edge === "bottom" ? "scaleY(-1)" : undefined }}
    >
      <path d={rimPath} fill={rim} />
      <path d={body} fill={color} />
    </svg>
  );
}

/** clip-path polygon with a ragged outline, used for photo frames. */
export function tornPolygon(seed: number, amp = 1.6, step = 2.6): string {
  const r = rng(seed);
  const p: string[] = [];
  const j = () => (r() * amp).toFixed(2);
  for (let x = 0; x < 100; x += step * (0.6 + r())) {
    p.push(`${x.toFixed(2)}% ${j()}%`);
  }
  for (let y = 0; y < 100; y += step * (0.6 + r())) {
    p.push(`${(100 - +j()).toFixed(2)}% ${y.toFixed(2)}%`);
  }
  for (let x = 100; x > 0; x -= step * (0.6 + r())) {
    p.push(`${x.toFixed(2)}% ${(100 - +j()).toFixed(2)}%`);
  }
  for (let y = 100; y > 0; y -= step * (0.6 + r())) {
    p.push(`${j()}% ${y.toFixed(2)}%`);
  }
  return `polygon(${p.join(",")})`;
}

/** Symmetric scroll flourish with a lotus at its centre. */
export function Flourish({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  const half = (
    <g
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    >
      <path d="M86 15 C78 15 74 9 67 9 C60 9 57 15 51 15 C46 15 44 11 46 8.5 C48 6 52 8 50.5 10.5" />
      <path d="M67 9 C70 4 76 3 79 5" />
      <path d="M51 15 C45 19 37 19 31 15.5 C27 13 21 13.5 18 16" />
      <path d="M31 15.5 C33 20 29 23 26 21" />
      <path d="M18 16 L2 16" strokeWidth="0.9" />
    </g>
  );
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 200 26"
      aria-hidden="true"
    >
      {half}
      <g transform="translate(200 0) scale(-1 1)">{half}</g>
      <g fill="currentColor">
        <path d="M100 3 C104 8 104 14 100 19 C96 14 96 8 100 3Z" />
        <path
          d="M99 19 C93 18 89 13 88.5 8.5 C93 10 97 14 99 19Z"
          opacity=".85"
        />
        <path
          d="M101 19 C107 18 111 13 111.5 8.5 C107 10 103 14 101 19Z"
          opacity=".85"
        />
        <path
          d="M90 20.5 C96 22.5 104 22.5 110 20.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
        />
      </g>
    </svg>
  );
}

/** Small leaf sprig used beside "Supported by". */
export function LeafSprig({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 48 40"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    >
      <path d="M4 36 C14 28 24 18 40 6" />
      <path
        d="M14 28 C10 22 12 16 17 14 C19 20 18 25 14 28Z"
        fill="currentColor"
        fillOpacity=".25"
      />
      <path
        d="M22 21 C19 14 22 8 27 7 C28 13 26 18 22 21Z"
        fill="currentColor"
        fillOpacity=".25"
      />
      <path
        d="M30 14 C29 8 32 4 37 3 C37 8 34 12 30 14Z"
        fill="currentColor"
        fillOpacity=".25"
      />
      <path
        d="M18 25 C24 26 29 24 32 20 C26 19 21 21 18 25Z"
        fill="currentColor"
        fillOpacity=".25"
      />
      <path
        d="M27 17 C32 18 37 16 40 12 C34 11 30 13 27 17Z"
        fill="currentColor"
        fillOpacity=".25"
      />
    </svg>
  );
}

/** Filigree corner; rotate via CSS for each corner. */
export function CornerScroll({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 90 120"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    >
      <path d="M8 118 V40 C8 20 22 8 42 8 H88" />
      <path d="M14 112 V44 C14 26 26 14 44 14 H70" opacity=".6" />
      <path d="M8 40 C20 40 28 48 26 58 C24 66 14 66 13 59 C12 53 19 51 21 56" />
      <path d="M42 8 C42 20 50 28 60 26 C68 24 68 14 61 13 C55 12 53 19 58 21" />
      <path d="M8 84 C16 80 22 84 20 90 C18 95 12 93 13 89" />
      <path d="M26 58 C34 62 38 70 34 78" opacity=".7" />
      <path d="M60 26 C64 34 72 36 78 32" opacity=".7" />
      <circle cx="30" cy="30" r="3" fill="currentColor" fillOpacity=".5" />
    </svg>
  );
}

/** Radial lotus mandala for the faint side decoration. */
export function Mandala({ className }: { className?: string }) {
  const petals = Array.from({ length: 16 }, (_, i) => i * 22.5);
  return (
    <svg
      className={className}
      viewBox="-100 -100 200 200"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    >
      <circle r="96" />
      <circle r="88" strokeDasharray="2 4" />
      {petals.map(a => (
        <g key={a} transform={`rotate(${a})`}>
          <path d="M0 -84 C12 -70 12 -52 0 -40 C-12 -52 -12 -70 0 -84Z" />
          <path d="M0 -76 C6 -66 6 -54 0 -47" />
        </g>
      ))}
      <circle r="38" />
      {petals.map(a => (
        <path
          key={`s${a}`}
          transform={`rotate(${a + 11.25})`}
          d="M0 -36 C7 -28 7 -20 0 -14 C-7 -20 -7 -28 0 -36Z"
        />
      ))}
      <circle r="12" />
      <circle r="5" fill="currentColor" />
    </svg>
  );
}

/** Line drawing of a Jain temple (shikhara), used as an icon and as a watermark. */
export function Temple({
  className,
  strokeWidth = 1.4,
}: {
  className?: string;
  strokeWidth?: number;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 80 70"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <path d="M40 2 V8 M40 3 L47 5 L40 7" />
      <path d="M34 22 C34 13 37 9 40 8 C43 9 46 13 46 22 Z" />
      <path d="M22 30 C22 24 25 21 28 20 C31 21 34 24 34 30 M46 30 C46 24 49 21 52 20 C55 21 58 24 58 30" />
      <path d="M12 38 C12 33 14 30 17 29 C20 30 22 33 22 38 M58 38 C58 33 60 30 63 29 C66 30 68 33 68 38" />
      <path d="M30 22 H50 V30 H30 Z" />
      <path d="M18 30 H62 V38 H18 Z" />
      <path d="M8 38 H72 V44 H8 Z" />
      <path d="M12 44 V62 M24 44 V62 M56 44 V62 M68 44 V62" />
      <path d="M32 62 V52 C32 47 36 45 40 45 C44 45 48 47 48 52 V62" />
      <path d="M4 62 H76 V67 H4 Z" />
    </svg>
  );
}

/** Temple archway with pillars, used as a faint corner silhouette (hero, footer). */
export function Archway({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 140 220"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
    >
      <path d="M70 6 V16 M70 8 L78 10 L70 12" />
      <path d="M50 46 C50 28 60 18 70 16 C80 18 90 28 90 46 Z" />
      <path d="M44 46 H96 V56 H44 Z" />
      <path d="M20 56 H120 V66 H20 Z" />
      <path d="M26 66 V200 M38 66 V200 M102 66 V200 M114 66 V200" />
      <path d="M48 200 V112 C48 92 58 82 70 82 C82 82 92 92 92 112 V200" />
      <path
        d="M54 200 V114 C54 98 61 90 70 90 C79 90 86 98 86 114 V200"
        opacity=".6"
      />
      <path d="M64 140 H76 V156 H64 Z M60 156 H80 V160 H60 Z" />
      <path d="M10 200 H130 V210 H10 Z M4 210 H136 V218 H4 Z" />
      <path
        d="M26 80 H38 M26 120 H38 M26 160 H38 M102 80 H114 M102 120 H114 M102 160 H114"
        opacity=".6"
      />
    </svg>
  );
}

/** Small lotus used on the footer divider. */
export function Lotus({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 64 34"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    >
      <path d="M32 3 C38 10 38 20 32 28 C26 20 26 10 32 3Z" />
      <path d="M30 28 C21 27 15 20 14 12 C21 14 27 20 30 28Z" />
      <path d="M34 28 C43 27 49 20 50 12 C43 14 37 20 34 28Z" />
      <path d="M28 29 C18 30 9 26 3 19 C12 18 21 22 28 29Z" />
      <path d="M36 29 C46 30 55 26 61 19 C52 18 43 22 36 29Z" />
      <path d="M16 31 H48" />
    </svg>
  );
}

/** Frangipani blossom placed at the foot of the About collage. */
export function Frangipani({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="-50 -50 100 100" aria-hidden="true">
      <defs>
        <radialGradient
          id="fg-c"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="scale(26)"
        >
          <stop offset="0" stopColor="#f2c14e" />
          <stop offset=".55" stopColor="#f8e6a8" />
          <stop offset="1" stopColor="#fffdf6" />
        </radialGradient>
      </defs>
      <ellipse
        cx="18"
        cy="38"
        rx="20"
        ry="8"
        fill="#3c6e2a"
        transform="rotate(-25 18 38)"
      />
      <ellipse
        cx="-24"
        cy="34"
        rx="18"
        ry="7"
        fill="#4c8434"
        transform="rotate(30 -24 34)"
      />
      {[0, 72, 144, 216, 288].map(a => (
        <path
          key={a}
          transform={`rotate(${a})`}
          d="M0 0 C10 -6 22 -22 16 -36 C10 -46 -4 -40 -6 -26 C-7 -16 -4 -8 0 0Z"
          fill="url(#fg-c)"
          stroke="#e9dcc0"
          strokeWidth="1"
        />
      ))}
      <circle r="5" fill="#eeb53a" />
    </svg>
  );
}

/** Open manuscript on a wooden reading stand (Guruvani illustration). */
export function OpenBookOnStand({ className }: { className?: string }) {
  const lines = (x: number) =>
    Array.from({ length: 9 }, (_, i) => (
      <path
        key={`${x}-${i}`}
        d={`M${x} ${30 + i * 6} h${x < 120 ? 62 - (i % 3) * 4 : 60 - (i % 2) * 6}`}
        stroke="#8a6a44"
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity={0.55}
      />
    ));
  return (
    <svg className={className} viewBox="0 0 240 130" aria-hidden="true">
      <defs>
        <linearGradient id="ob-page-l" x1="0" x2="1">
          <stop offset="0" stopColor="#e6cf9f" />
          <stop offset=".85" stopColor="#f7ead0" />
          <stop offset="1" stopColor="#c9ab78" />
        </linearGradient>
        <linearGradient id="ob-page-r" x1="1" x2="0">
          <stop offset="0" stopColor="#e6cf9f" />
          <stop offset=".85" stopColor="#f7ead0" />
          <stop offset="1" stopColor="#c9ab78" />
        </linearGradient>
        <linearGradient id="ob-wood" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#7a3d16" />
          <stop offset="1" stopColor="#4a220a" />
        </linearGradient>
      </defs>
      <ellipse cx="120" cy="124" rx="96" ry="5" fill="#5b3214" opacity=".25" />
      {/* stand */}
      <path d="M58 92 L182 92 L170 104 L70 104 Z" fill="url(#ob-wood)" />
      <path
        d="M78 104 L66 122 M162 104 L174 122 M100 104 L96 122 M140 104 L144 122"
        stroke="#4a220a"
        strokeWidth="5"
        strokeLinecap="round"
      />
      {/* cover */}
      <path
        d="M22 26 C60 16 100 18 120 28 C140 18 180 16 218 26 L222 96 C182 88 140 90 120 98 C100 90 58 88 18 96 Z"
        fill="#6b2c12"
      />
      {/* page stacks */}
      <path
        d="M26 22 C62 12 100 14 120 25 L120 94 C100 86 62 84 24 92 Z"
        fill="#d9bd8a"
      />
      <path
        d="M214 22 C178 12 140 14 120 25 L120 94 C140 86 178 84 216 92 Z"
        fill="#d9bd8a"
      />
      <path
        d="M30 18 C64 8 102 10 120 22 L120 90 C102 80 64 79 28 88 Z"
        fill="url(#ob-page-l)"
      />
      <path
        d="M210 18 C176 8 138 10 120 22 L120 90 C138 80 176 79 212 88 Z"
        fill="url(#ob-page-r)"
      />
      {lines(46)}
      {lines(134)}
      <path d="M120 22 V90" stroke="#9a7444" strokeWidth="1.2" />
    </svg>
  );
}

/** Pile of antique books drawn in CSS-free SVG, mirrored for the right side. */
export function BookPile({
  className,
  flip = false,
}: {
  className?: string;
  flip?: boolean;
}) {
  const lying = [
    { y: 128, w: 150, h: 18, c: "#6e3a1c", x: 4 },
    { y: 110, w: 136, h: 18, c: "#8a5228", x: 12 },
    { y: 94, w: 142, h: 16, c: "#5b2c12", x: 6 },
  ];
  const standing = [
    { x: 160, w: 22, h: 108, c: "#7b2e18" },
    { x: 184, w: 18, h: 96, c: "#9b6a3a" },
    { x: 204, w: 24, h: 118, c: "#5e2a12" },
    { x: 230, w: 20, h: 102, c: "#a07848" },
  ];
  return (
    <svg
      className={className}
      viewBox="0 0 260 150"
      aria-hidden="true"
      style={flip ? { transform: "scaleX(-1)" } : undefined}
    >
      <ellipse cx="130" cy="147" rx="128" ry="4" fill="#4a2a10" opacity=".25" />
      {lying.map((b, i) => (
        <g key={i}>
          <rect x={b.x} y={b.y} width={b.w} height={b.h} rx="2" fill={b.c} />
          <rect
            x={b.x + b.w - 8}
            y={b.y + 2}
            width="7"
            height={b.h - 4}
            fill="#ecd9b2"
          />
          <path
            d={`M${b.x + 14} ${b.y + 3} v${b.h - 6} M${b.x + 20} ${b.y + 3} v${b.h - 6}`}
            stroke="#d4a85c"
            strokeWidth="1.4"
            opacity=".7"
          />
        </g>
      ))}
      {standing.map((b, i) => (
        <g key={`s${i}`}>
          <rect
            x={b.x}
            y={146 - b.h}
            width={b.w}
            height={b.h}
            rx="2"
            fill={b.c}
          />
          <path
            d={`M${b.x + 3} ${146 - b.h + 12} h${b.w - 6} M${b.x + 3} ${146 - b.h + 16} h${b.w - 6} M${b.x + 3} 128 h${b.w - 6} M${b.x + 3} 132 h${b.w - 6}`}
            stroke="#d4a85c"
            strokeWidth="1.3"
            opacity=".75"
          />
          <rect
            x={b.x + 4}
            y={146 - b.h + 30}
            width={b.w - 8}
            height="14"
            rx="1"
            fill="#000"
            opacity=".15"
          />
        </g>
      ))}
    </svg>
  );
}

/** Line–lotus–line ornament used under the CTA buttons and under the Navkar text. */
export function LotusRule({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 320 30" aria-hidden="true">
      <g stroke="#7f4719" strokeWidth="1.4" strokeLinecap="round">
        <path d="M6 16 H118 M202 16 H314" />
      </g>
      <g fill="#7f4719">
        <circle cx="128" cy="16" r="3" />
        <circle cx="192" cy="16" r="3" />
        <path
          d="M160 3 C166 10 166 18 160 25 C154 18 154 10 160 3Z"
          fill="#b8692a"
        />
        <path
          d="M158 25 C150 24 144 18 143 11 C150 13 156 18 158 25Z"
          fill="#b8692a"
        />
        <path
          d="M162 25 C170 24 176 18 177 11 C170 13 164 18 162 25Z"
          fill="#b8692a"
        />
        <path
          d="M156 26 C148 27 140 24 135 18 C142 17 150 20 156 26Z"
          fill="#c97a36"
        />
        <path
          d="M164 26 C172 27 180 24 185 18 C178 17 170 20 164 26Z"
          fill="#c97a36"
        />
      </g>
    </svg>
  );
}

/** Arched temple gateway used as the logo mark in the header. */
export function TempleGateLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 48 56"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    >
      <path d="M24 1 V6 M24 2 L29 3.5 L24 5" />
      <path d="M14 16 C14 9 19 6 24 6 C29 6 34 9 34 16 Z" />
      <path d="M8 16 H40 V20 H8 Z" />
      <path d="M10 20 V50 M38 20 V50 M14 20 V50 M34 20 V50" />
      <path d="M18 50 V34 C18 29 21 26 24 26 C27 26 30 29 30 34 V50" />
      <path
        d="M21 50 V36 C21 33 22.5 31 24 31 C25.5 31 27 33 27 36 V50"
        opacity=".6"
      />
      <path d="M4 50 H44 V54 H4 Z" />
    </svg>
  );
}
